import { any, bool, func, oneOf, string } from 'prop-types'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect, { components } from 'react-select'
import { baseAPI } from 'services/api'
import styled, { keyframes } from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
const animation1 = keyframes`
   0% {
      clip-path: polygon(0% 0%, 0% 0%, 0% 0%, 50% 50%, 0% 0%, 0% 0%, 0% 0%);
   }
   50% {
      clip-path: polygon(
         0% 0%,
         0% 100%,
         0% 100%,
         50% 50%,
         100% 0%,
         100% 0%,
         0% 0%
      );
   }
   100% {
      clip-path: polygon(
         0% 0%,
         0% 100%,
         100% 100%,
         50% 50%,
         100% 100%,
         100% 0%,
         0% 0%
      );
   }
`
const animation2 = keyframes`
   to {
      transform: rotate(1turn);
   }
`
const StyledControl = styled.div`
	display: flex;
	height: 100%;
	width: 100%;
	& .inner-content {
		height: 100%;
		padding: 0 0 0 16px;
		width: calc(100% - 66px);
		&[data-size='large'] {
			padding: 0 0 0 20px;
		}
		&[data-size='small'] {
			padding: 0 0 0 12px;
		}
		& input {
			opacity: 1 !important;
			min-width: 100% !important;
			max-width: 100% !important;
			display: flex;
		}
	}
	& .indicator-content {
		align-items: center;
		display: flex;
		height: 100%;
		justify-content: flex-end;
		padding: 0 12px 0 8px;
		width: 66px;
		&[data-size='large'] {
			padding: 0 16px 0 8px;
		}
		&[data-size='small'] {
			padding: 0 8px 0 8px;
		}
		& .loading-content {
			align-items: center;
			display: flex;
			height: 23px;
			justify-content: center;
			width: 23px;
			& .loading {
				height: 23px;
				width: 23px;
				& div {
					animation: ${animation1} 0.7s linear alternate infinite,
						${animation2} 0.3s linear infinite;
					border-radius: 50%;
					border: 2px solid var(--main);
					box-sizing: border-box;
					height: 23px;
					width: 23px;
				}
			}
		}
		& .clear-button {
			align-items: center;
			display: flex;
			height: 23px;
			justify-content: center;
			width: 23px;
			& button {
				align-items: center;
				background-color: transparent;
				border-radius: 50%;
				border: none;
				color: #808080;
				cursor: pointer;
				display: flex;
				height: 23px;
				justify-content: center;
				outline: none;
				padding: 0;
				width: 23px;
			}
		}
		& .dropdown-button {
			align-items: center;
			background-color: transparent;
			color: #808080;
			cursor: pointer;
			display: flex;
			height: 23px;
			justify-content: center;
			width: 23px;
		}
	}
`
const StyledOption = styled.div`
	.dark & .title {
		color: #ffffff;
	}
	&[data-selected='true'] {
		& .sub-title,
		& .title {
			color: #ffffff;
		}
	}
	&[data-size='large'] {
		& .title {
			font-size: 20px;
		}
		& .sub-title {
			font-size: 16px;
		}
	}
	&[data-size='small'] {
		& .title {
			font-size: 16px;
		}
		& .sub-title {
			font-size: 14px;
		}
	}
	& .title {
		color: #292b32;
		font-size: 18px;
		font-weight: 500;
	}
	& .sub-title {
		color: #949494;
		font-size: 15px;
		font-weight: 400;
	}
`
const Control = props => {
	const children = props?.children
	const inputValue = props?.selectProps?.inputValue
	const isDisabled = props?.isDisabled
	const isFocused = props?.isFocused
	const isLoading = props?.selectProps?.isLoading
	const onChange = props?.selectProps?.onInputChange
	const size = props?.selectProps?.size
	return (
		<components.Control {...props}>
			<StyledControl>
				<div className='inner-content' data-size={size}>
					{children}
				</div>
				<div className='indicator-content' data-size={size}>
					{isLoading && !isDisabled ? (
						<div className='loading-content'>
							<div className='loading'>
								<div></div>
							</div>
						</div>
					) : inputValue && !isDisabled ? (
						<div className='clear-button'>
							<button
								onClick={() => {
									onChange('', { action: 'set-value' })
								}}
								type='button'
								data-focused={isFocused}
							>
								<svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
									<path
										d='M12 4L4 12'
										stroke='#949494'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
									/>
									<path
										d='M4 4L12 12'
										stroke='#949494'
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth='2'
									/>
								</svg>
							</button>
						</div>
					) : null}
					<div className='dropdown-button'>
						<svg height='18' width='18' viewBox='0 0 20 20'>
							<path
								fill='#949494'
								d='M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z'
							></path>
						</svg>
					</div>
				</div>
			</StyledControl>
		</components.Control>
	)
}
const IndicatorsContainer = () => null
const Menu = ({ isLoading, children, ...props }) =>
	!isLoading && <components.Menu {...props}>{children}</components.Menu>
const Input = props => (
	<components.Input {...props} data-cy={props?.selectProps?.['data-cy']} />
)
const Option = props => {
	const isSelected = props?.isSelected
	const AttributeName = props?.data?.apiData?.attributeName
	const BrandName = props?.data?.apiData?.brandName
	const ClassName = props?.data?.apiData?.className
	const GroupName = props?.data?.apiData?.groupName
	const MxikCode = props?.data?.apiData?.mxikCode
	const mxikFullName = props?.data?.apiData?.mxikFullName
	const PositionName = props?.data?.apiData?.positionName
	const SubPositionName = props?.data?.apiData?.subPositionName
	const size = props?.selectProps?.size
	return (
		<components.Option
			{...props}
			innerProps={Object.assign({}, props?.innerProps, {
				'data-cy': props?.selectProps?.['data-cy'] + '-' + props?.value,
			})}
		>
			<StyledOption data-size={size} data-selected={isSelected}>
				{mxikFullName ? <div className='title'>{mxikFullName}</div> : ''}
				{MxikCode ? <div className='title'>ИКПУ: {MxikCode}</div> : ''}
				{GroupName ? <div className='sub-title'>Группа: {GroupName}</div> : ''}
				{ClassName ? <div className='sub-title'>Класс: {ClassName}</div> : ''}
				{PositionName ? (
					<div className='sub-title'>Позиция: {PositionName}</div>
				) : (
					''
				)}
				{SubPositionName ? (
					<div className='sub-title'>Субпозиция: {SubPositionName}</div>
				) : (
					''
				)}
				{AttributeName ? (
					<div className='sub-title'>
						Наименование атрибута: {AttributeName}
					</div>
				) : (
					''
				)}
				{BrandName ? <div className='sub-title'>Бренд: {BrandName}</div> : ''}
			</StyledOption>
		</components.Option>
	)
}
const defaultProps = {
	components: { Control, IndicatorsContainer, Menu, Input, Option },
	className: 'ikpu-auto-complete-input',
	filterOption: option => option,
	isClearable: true,
	isMulti: false,
	isSearchable: true,
	maxMenuHeight: 250,
	menuPlacement: 'auto',
	menuPortalTarget: document.body,
}
const styles = {
	container: (styles, props) => ({
		...styles,
		width: '100%',
		height:
			props?.selectProps?.size === 'large'
				? 52
				: props?.selectProps?.size === 'small'
				? 36
				: 44,
	}),
	valueContainer: (styles, props) => ({
		...styles,
		display: 'flex',
		padding: 0,
		height:
			props?.selectProps?.size === 'large'
				? 50
				: props?.selectProps?.size === 'small'
				? 34
				: 42,
	}),
	placeholder: (styles, props) => ({
		...styles,
		alignItems: 'center',
		color: '#717171',
		display: 'flex',
		fontWeight: 500,
		height: '100%',
		left: 0,
		margin: 0,
		position: 'absolute',
		top: 0,
		fontSize:
			props?.selectProps?.size === 'large'
				? 18
				: props?.selectProps?.size === 'small'
				? 14
				: 16,
		padding:
			props?.selectProps?.size === 'large'
				? '0 12px 0 0'
				: props?.selectProps?.size === 'small'
				? '0 4px 0 0'
				: '0 8px 0 0',
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	input: (styles, { isDisabled, selectProps }) => ({
		...styles,
		color: isDisabled ? '#717171' : '#000000',
		fontWeight: 500,
		height: '100%',
		left: 0,
		margin: 0,
		padding: 0,
		position: 'absolute',
		top: 0,
		visibility: 'visible',
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: 16,
		'.dark &': {
			color: isDisabled ? '#aaaaaa' : '#e1e1e1',
		},
	}),
	menuPortal: styles => ({ ...styles, zIndex: 9999 }),
	menuList: styles => ({
		...styles,
		padding: 5,
		'::-webkit-scrollbar': {
			width: 6,
		},
		'::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'::-webkit-scrollbar-thumb': {
			backgroundColor: 'var(--main)',
			borderRadius: 3,
		},
	}),
	noOptionsMessage: (styles, props) => ({
		...styles,
		color: '#000000',
		cursor: 'default',
		fontWeight: 500,
		textAlign: 'left',
		width: '100%',
		padding:
			props?.selectProps?.size === 'large'
				? 16
				: props?.selectProps?.size === 'small'
				? 8
				: 12,
		fontSize:
			props?.selectProps?.size === 'large'
				? 18
				: props?.selectProps?.size === 'small'
				? 14
				: 16,
		height:
			props?.selectProps?.size === 'large'
				? 52
				: props?.selectProps?.size === 'small'
				? 36
				: 44,
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	option: (styles, { isSelected, isDisabled, isFocused, selectProps }) => ({
		...styles,
		borderRadius: 10,
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		transition: 200,
		whiteSpace: 'nowrap',
		width: '100%',
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: 16,
		padding:
			selectProps?.size === 'large'
				? '14px 16px'
				: selectProps?.size === 'small'
				? '8px 10px'
				: 12,
		backgroundColor: isDisabled
			? '#f7f8fc'
			: isSelected
			? 'var(--main)'
			: isFocused
			? 'var(--success-20)'
			: '#ffffff',
		color: isDisabled
			? '#696f85'
			: isSelected
			? '#ffffff'
			: isFocused
			? '#000000'
			: '#000000',
		':hover': {
			backgroundColor: isDisabled
				? '#f7f8fc'
				: isSelected
				? 'var(--main)'
				: 'var(--success-20)',
		},
		'.dark &': {
			color: '#ffffff',
			backgroundColor: isDisabled
				? '#333333'
				: isSelected
				? '#018ECC'
				: isFocused
				? '#41932133'
				: 'transparent',
			':hover': {
				backgroundColor: isDisabled
					? '#333333'
					: isSelected
					? '#018ECC'
					: '#41932133',
			},
		},
	}),
}
const IkpuAutoComplete = memo(
	({
		'data-cy': dataCY,
		hideBorder = false,
		isDisabled = false,
		isError = false,
		menuWidth,
		noOptionsMessage = null,
		onChange,
		onFocus,
		placeholder = '',
		size = 'medium',
		value = null,
	}) => {
		const { t } = useTranslation('components')
		const [loading, setLoading] = useState(false)
		const [options, setOptions] = useState([])
		const debouncedSearch = useDebouncedCallback(search => {
			setLoading(true)
			const language = localStorage.getItem('i18nextLng') || 'uz'
			const searchText = search ? '&search_text=' + search : '&search_text=-'
			const url = `/edo_v2/tasnif-integration/search-ikpu-code/?page=0&page_size=100&lang=${language}${searchText}`
			baseAPI({ url })
				.then(response => {
					const Items = response?.data?.data
					const newItems = Array.isArray(Items) ? Items : []
					const options = newItems.map(apiData => ({
						apiData,
						value: apiData?.mxikCode,
						label: apiData?.mxikFullName,
					}))
					setOptions(options)
				})
				.catch(() => setOptions([]))
				.finally(() => setLoading(false))
		}, 300)
		const onInputChange = (option, { action }) => {
			if (action !== 'input-blur' && action !== 'menu-close') {
				onChange(option)
			}
		}
		const NoOptionsMessage =
			typeof noOptionsMessage === 'function'
				? noOptionsMessage
				: () => t('no-data')
		useEffect(() => {
			if (!isDisabled) {
				const search = typeof value === 'string' ? value : value?.label
				debouncedSearch(search)
			}
		}, [value, debouncedSearch, isDisabled])
		return (
			<ReactSelect
				{...defaultProps}
				data-cy={dataCY}
				inputValue={typeof value === 'string' ? value : value?.label}
				isDisabled={isDisabled}
				isLoading={loading}
				noOptionsMessage={NoOptionsMessage}
				onChange={onChange}
				onFocus={onFocus}
				onInputChange={onInputChange}
				options={options}
				placeholder={placeholder}
				size={size}
				value={null}
				styles={{
					...styles,
					control: (styles, { isFocused, isDisabled, selectProps }) => ({
						...styles,
						backgroundColor: isDisabled ? '#f4f4f4' : '#ffffff',
						borderRadius: 10,
						borderStyle: 'solid',
						borderWidth: hideBorder ? 0 : 1.5,
						boxShadow: 'none',
						color: 'rgb(37, 42, 59)',
						cursor: isDisabled ? 'default' : 'text',
						minWidth: 100,
						outline: 'none',
						padding: 0,
						transition: 0,
						width: '100%',
						minHeight:
							selectProps?.size === 'large'
								? 52
								: selectProps?.size === 'small'
								? 36
								: 44,
						height:
							selectProps?.size === 'large'
								? 52
								: selectProps?.size === 'small'
								? 36
								: 44,
						borderColor: isFocused
							? '#018ECC'
							: isError
							? '#e41d32'
							: '#e1e1e1',
						':hover': { borderColor: '#018ECC' },
						'.dark &': {
							backgroundColor: '#2E2D2D',
							color: '#e1e1e1',
							borderColor: isFocused
								? '#018ECC'
								: isError
								? '#e41d32'
								: '#555555',
							':hover': { borderColor: '#018ECC' },
						},
					}),
					menu: styles => ({
						...styles,
						...(menuWidth ? { width: menuWidth } : {}),
						backgroundColor: '#ffffff',
						border: 'none',
						borderRadius: 12,
						boxShadow:
							'0 1px 20px 0 rgba(13, 46, 105, 0.07), 0 1px 20px 0 rgba(13, 46, 105, 0.07)',
						margin: 0,
						overflow: 'hidden',
						padding: 0,
						'.dark &': {
							backgroundColor: '#333333',
							boxShadow:
								'0 1px 20px 0px rgba(0, 0, 0, 0.07), 0 1px 20px 0px rgba(0, 0, 0, 0.07)',
						},
					}),
				}}
			/>
		)
	}
)
IkpuAutoComplete.propTypes = {
	'data-cy': string,
	getResponse: func,
	getUrl: func,
	isDisabled: bool,
	isError: bool,
	menuWidth: string,
	noOptionsMessage: any,
	onChange: func,
	onFocus: func,
	placeholder: string,
	size: oneOf(['large', 'medium', 'small']),
	value: any,
}
export default IkpuAutoComplete
